<template lang="pug">
form.prose.dark_prose-invert.p-8.bg-gray-100.dark_bg-gray-800.mb-10.relative
  button.btn.btn-icon.opacity-50.aspect-square.flex.items-center.justify-center.font-bold.text-xl.w-8.h-8.absolute.top-4.right-4(
    @click="emit('close')"
    ref="formRef"
    type="button"
  ) &times;
  h2.mt-2.mb-10 {{ activityNameModel === 'Resource' ? 'Create a new resource' : 'Create a new course' }}

  hr.my-6
  
  fieldset
    legend
      p.font-bold.mt-0 Product type
    .flex.gap-4.items-center
      label.flex.gap-1.items-center
        input.focus-visible_ring-4.transition.rounded-full(
          name="activityNameModel"
          type="radio"
          v-model="activityNameModel"
          value="Full Course"
        )
        span Full Course

      label.flex.gap-1.items-center
        input.focus-visible_ring-4.transition.rounded-full(
          name="activityNameModel"
          type="radio"
          v-model="activityNameModel"
          value="Conversational Learning"
        )
        span Conversational Learning

      label.flex.gap-1.items-center
        input.focus-visible_ring-4.transition.rounded-full(
          name="activityNameModel"
          type="radio"
          v-model="activityNameModel"
          value="Resource"
        )
        span Resource

  hr.my-2

  fieldset
      legend
        p.font-bold.mt-0 {{ activityNameModel === 'Resource' ? 'Resource type' : 'Course type' }}
      
      // Course Type Options
      template(v-if="activityNameModel !== 'Resource'")
        .flex.gap-4.items-center
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(
              name="type"
              type="radio"
              v-model="type"
              value="course"
            )
            span Base course

          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(
              name="type"
              type="radio"
              v-model="type"
              value="bespoke"
            )
            span Bespoke course

          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(
              name="type"
              type="radio"
              v-model="type"
              value="custom"
            )
            span Customisation
        p.text-sm.max-w-md.opacity-70 Is this a VinciWorks base course or a client&rsquo;s bespoke project?
      
      // Resource Type Options
      template(v-else)
        .flex.flex-col.gap-2
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Blog")
            span Blog
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Guide")
            span Guide
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Template")
            span Template
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Video")
            span Video
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Webinar")
            span Webinar
          label.flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(type="radio" name="resourceType" v-model="resourceType" value="Playlist")
            span Playlist
        p.text-sm.max-w-md.opacity-70 Which type of resource will this be? This selection cannot be changed moving forward.

  hr.my-2

  fieldset
    label
      .flex.gap-6.items-center.mb-2.justify-between
        p.font-bold {{ activityNameModel === 'Resource' ? 'Resource ID number' : 'Course ID number' }}
        //- Generate ID Button (for base courses and bespoke courses)
        button.btn.bg-primary-600.text-white.px-4.py-2.rounded.mt-2.flex.items-center(
          v-if="type === 'course' || type === 'bespoke' || activityNameModel === 'Resource'"
          @click="generateCourseId"
          :disabled="isGeneratingId || checkingIfCourseExists || (type === 'bespoke' && !validFirmCode)"
          type="button"
        )
          span.mr-2(v-if="isGeneratingId")
            Icon(icon="mdi:loading" class="animate-spin")
          span {{ isGeneratingId ? 'Generating...' : 'Generate ID' }}      
      input#newCourseId.w-full.p-2(
        :class="courseIdInputClasses"
        inputmode="numeric"
        pattern="[0-9]*"
        :placeholder="activityNameModel === 'Resource' ? '2000000' : (type === 'bespoke' ? '1001000' : '123')"
        ref="courseIdInput"
        type="text"
        v-model="courseId"
      )
      
      p.text-sm.max-w-md.opacity-70(
        v-if="type === 'bespoke'"
      ) It is recommended that the Course ID be between 1,000,000 and 1,999,999 for new bespoke courses.
      p.text-sm.max-w-md.opacity-70(
        v-else
      ) You can generate an ID using the Generate ID button or enter one manually.

  hr.my-2(v-if="activityNameModel === 'Resource'")

  fieldset(v-if="activityNameModel === 'Resource'")
    label
      p.font-bold Resource title
      input.w-full.p-2(
        type="text"
        v-model="resourceTitle"
        placeholder="Enter resource title"
      )
      p.text-sm.max-w-md.opacity-70 Enter a title for this resource.

  hr.my-2

  fieldset(v-if="activityNameModel !== 'Resource'")
    label(:class="{ 'opacity-20': type === 'course' }").transition
      p.font-bold Client firm code
      input.w-full.p-2(
        :class="[{ '!ring-red-700': !validFirmCode }, { '!ring-orange-500': type !== 'course' && courseAlreadyExists }]"
        placeholder="qrstuv"
        ref="firmCodeInput"
        type="text" 
        :value="firmCode"
        @input="onFirmCodeInput"
        :disabled="type === 'course'"
      )
      p.text-sm.max-w-md.opacity-70 The firm code needs to match the lowercase "company code" in HubSpot and VinciWorks Portal. Please verify!

    hr.my-8

    label
      p.font-bold Course YAML
      textarea.w-full.p-2.font-monospace(rows="8" type="text" v-model="yaml")
      p.text-sm 
        span Paste some YAML to get started!
        span.opacity-50.ms-2 (Optional)

  p.flex.gap-6.items-center.flex-row-reverse
    button.btn.btn-primary.px-4.py-2.bg-primary-600.text-white.btn-hover_bg-opacity-90.active_bg-opacity-80(
      :disabled="submitStatus === 'pending' || submitStatus === 'success' || !validCourseId || !validFirmCode || !validResourceTitle || !validResourceType || checkingIfCourseExists || courseAlreadyExists || isGeneratingId"
      @click="submit"
      type="button"
    ) 
      span Create
    
    p.text-sm.animate-loading(
      v-if="checkingIfCourseExists"
    ) Checking if course exists...
    
    p.text-sm(
      :class="[{ 'text-red-700': submitStatus === 'error' || !validCourseId || !validFirmCode || !validResourceTitle || !validResourceType }, { 'text-orange-500': courseAlreadyExists }]"
      v-else=""
    ) {{ submitMessage }}
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import {
  commitDraft,
  createNewDraft,
  getNextBaseCourseId,
  getNextBespokeCourseId
} from '../lib/callModContentAuthors';
import { useEventListener } from '@vueuse/core';
import { setCourse } from '../store/courses';
import { useRouter } from 'vue-router';
import {
  getLatestDraftOrSaved,
  getLatestResourceDraftOrSaved
} from '../../lib/callModContent';
import { debounce } from 'lodash-es';
import extractTitle, { extractClTitle } from '../../lib/extractTitle';
import extractBaseCourse, {
  extractBaseClCourse
} from '../../lib/extractBaseCourse';
import { load } from 'js-yaml';
import { Icon } from '@iconify/vue';
import {
  getResourceTypeId,
  type ResourceTypeName
} from '../lib/resourceTypesUtil';

const router = useRouter();
const emit = defineEmits(['close']);
const props = defineProps<{
  defaultActivityName?: 'Full Course' | 'Conversational Learning' | 'Resource';
}>();

const courseId = ref('');
const firmCode = ref('');
const existingCourseTitle = ref('');
const courseAlreadyExists = ref(false);
const checkingIfCourseExists = ref(false);
const isGeneratingId = ref(false);
const activityNameModel = ref<CourseDraft['activityName']>(
  props.defaultActivityName || 'Full Course'
);
// Initialize type based on defaultActivityName prop
const type = ref<CourseDraft['type']>(
  props.defaultActivityName === 'Resource' ? 'resource' : 'course'
);
// Default to Blog if Resource is selected on init
const resourceType = ref<CourseDraft['resourceType']>(
  props.defaultActivityName === 'Resource' ? 'Blog' : undefined
);
const resourceTitle = ref('');
const yaml = ref('');
const submitStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');
const error = ref('');
const courseIdInput = ref<HTMLInputElement>();
const firmCodeInput = ref<HTMLInputElement>();
const formRef = ref<HTMLFormElement>();
const hasAutoFilled = ref(false);

// valid course IDs must be a positive integer
const validCourseId = computed(() => {
  // if (type.value === 'bespoke') {
  //   // Bespoke courses must be between 1,000,000 and 1,999,999
  //   const idNum = parseInt(courseId.value);
  //   return /^\d+$/.test(courseId.value) && idNum >= 1000000 && idNum <= 1999999;
  // } else {
  //   // Base courses and custom courses use existing validation (positive integer)
  return /^\d+$/.test(courseId.value) && parseInt(courseId.value) > 0;
  // }
});

// valid firm codes must only contain lowercase letters and numbers and not start with a number
// Resources don't need firm codes, so always return true for them
const validFirmCode = computed(
  () =>
    type.value === 'course' ||
    type.value === 'resource' ||
    (firmCode.value.length > 0 && /^[a-z][a-z0-9]*$/.test(firmCode.value))
);

// Resource title is required for resources
const validResourceTitle = computed(() => {
  return (
    activityNameModel.value !== 'Resource' ||
    resourceTitle.value.trim().length > 0
  );
});

// Resource type is required for resources
const validResourceType = computed(() => {
  return activityNameModel.value !== 'Resource' || !!resourceType.value;
});

const isConversational = computed(() => {
  return activityNameModel.value === 'Conversational Learning';
});

const courseIdInputClasses = computed(() => {
  if (courseAlreadyExists.value) {
    return '!ring-orange-500';
  }
  if (!validCourseId.value) {
    return '!ring-red-700';
  }
  return '';
});

const checkCourseExists = debounce(async () => {
  courseAlreadyExists.value = false;
  existingCourseTitle.value = '';
  checkingIfCourseExists.value = true;

  // Validation checks - resources don't need firmCode
  if (
    (type.value === 'course' && !validCourseId.value) ||
    (type.value === 'resource' && !validCourseId.value) ||
    (type.value !== 'course' &&
      type.value !== 'resource' &&
      (!validCourseId.value || !validFirmCode.value))
  ) {
    checkingIfCourseExists.value = false;
    return false;
  }

  const idNum = parseInt(courseId.value);

  // For resources, query by vinciResourceId
  if (type.value === 'resource') {
    const resourceResponse = await getLatestResourceDraftOrSaved({
      vinciResourceId: idNum
    });

    if (!resourceResponse) {
      checkingIfCourseExists.value = false;
      return false;
    }

    existingCourseTitle.value = resourceResponse?.title || '';
    courseAlreadyExists.value = resourceResponse?.vinciResourceId
      ? true
      : false;
    checkingIfCourseExists.value = false;
    return courseAlreadyExists.value;
  }

  // For courses, use the existing logic
  const cf: CourseAndFirm = {
    courseId: idNum,
    firmCode:
      firmCode.value && firmCode.value.length > 0 ? firmCode.value : undefined
  };

  const courseResponse = await getLatestDraftOrSaved(cf);

  if (!courseResponse) {
    checkingIfCourseExists.value = false;
    return false;
  }

  existingCourseTitle.value = courseResponse?.title || '';
  courseAlreadyExists.value = courseResponse?.courseId ? true : false;
  checkingIfCourseExists.value = false;
  return courseAlreadyExists.value;
}, 1000);

const onFirmCodeInput = (e: InputEvent) => {
  const input = e.target as HTMLInputElement;
  firmCode.value = input.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
  input.value = firmCode.value;
};

onMounted(() => {
  // No auto-focus or auto-generation - user must click Generate ID button first
});

// Generate course ID for base courses and bespoke courses (manual trigger)
const generateCourseId = async () => {
  isGeneratingId.value = true;
  try {
    let nextId: number;

    if (type.value === 'bespoke') {
      // For bespoke courses, get the next ID for this specific firm
      nextId = await getNextBespokeCourseId(firmCode.value);
    } else {
      // For base courses or resources, get the next base ID
      // Pass forResource=true if generating for a resource (will skip 5000-9999 range)
      // Check both type.value and activityNameModel to ensure we catch resources correctly
      const isResource =
        type.value === 'resource' || activityNameModel.value === 'Resource';
      nextId = await getNextBaseCourseId(isResource);
    }

    courseId.value = nextId.toString();
  } catch (error) {
    console.error('Failed to generate course ID:', error);
    // Fallback to manual entry - user can still enter ID manually
  } finally {
    isGeneratingId.value = false;
  }
};

useEventListener(courseIdInput, 'focus', () => (submitStatus.value = 'idle'));
useEventListener(firmCodeInput, 'focus', () => (submitStatus.value = 'idle'));

watch(type, () => {
  // reset the firm if we switch back to 'base course'
  if (type.value === 'course') {
    firmCode.value = '';
    // Clear course ID when switching to course - user must click Generate ID
    courseId.value = '';
    hasAutoFilled.value = false;
  } else if (type.value === 'bespoke') {
    // Reset course ID when switching to bespoke
    courseId.value = '';
    hasAutoFilled.value = false;
  } else {
    // For custom courses, reset the auto-fill flag so it can work again if switching back
    hasAutoFilled.value = false;
  }
  checkingIfCourseExists.value = true;
  checkCourseExists();
});

// Watch for course ID changes to verify course existence
watch(courseId, () => {
  if (courseId.value && validCourseId.value) {
    checkingIfCourseExists.value = true;
    checkCourseExists();
  }
});

watch(firmCode, () => {
  if (firmCode.value && validFirmCode.value) {
    checkingIfCourseExists.value = true;
    checkCourseExists();
  }
});

// Automatically set type based on activity name
watch(activityNameModel, newValue => {
  if (newValue === 'Conversational Learning') {
    type.value = 'course';
  } else if (newValue === 'Resource') {
    type.value = 'resource';
    // Default resource type to Blog when Resource is selected
    if (!resourceType.value) {
      resourceType.value = 'Blog';
    }
  }
});

const submitMessage = computed(() => {
  if (
    validCourseId.value &&
    validFirmCode.value &&
    validResourceTitle.value &&
    validResourceType.value &&
    courseAlreadyExists.value
  )
    return `Course already exists (${existingCourseTitle.value})`;
  else if (!validCourseId.value) {
    // if (type.value === 'bespoke') {
    //   return 'Please enter a course ID between 1,000,000 and 1,999,999.';
    // } else {
    return 'Please enter a valid course ID.';
    // }
  } else if (!validFirmCode.value) return 'Please enter a valid firm code.';
  else if (!validResourceType.value) return 'Please select a resource type.';
  else if (!validResourceTitle.value)
    return 'Please enter a title for this resource.';

  switch (submitStatus.value) {
    case 'pending':
      return 'Creating...';
    case 'success':
      return 'Success!';
    case 'error':
      return error.value || 'Something went wrong.';
    default:
      return ' ';
  }
});

const submit = async () => {
  if (checkingIfCourseExists.value || courseAlreadyExists.value) return;

  // Initialize course object
  const course: CourseDraft = {
    courseId: +courseId.value,
    type: type.value,
    activityName: activityNameModel.value,
    // For resources, use empty string (will be removed later); for courses use actual yaml
    yaml: type.value === 'resource' ? '' : yaml.value
  };

  // Handle resources differently
  if (type.value === 'resource') {
    course.vinciResourceId = +courseId.value;
    course.resourceType = resourceType.value;

    // Calculate and set resourceTypeId
    if (resourceType.value) {
      const resourceTypeId = getResourceTypeId(
        resourceType.value as ResourceTypeName
      );
      if (resourceTypeId !== undefined) {
        course.resourceTypeId = resourceTypeId;
      }
    }

    // Ensure title is set from resourceTitle form field
    if (resourceTitle.value && resourceTitle.value.trim()) {
      course.title = resourceTitle.value.trim();
    } else {
      console.error('Resource title is empty!', {
        resourceTitle: resourceTitle.value
      });
      error.value = 'Resource title is required';
      submitStatus.value = 'error';
      return;
    }
    // Remove yaml, firmCode, and courseId for resources (don't include in metadata)
    delete (course as any).yaml;
    delete (course as any).firmCode;
    delete (course as any).courseId;
    // Skip baseCourse logic for resources
  } else {
    // Existing course logic
    if (type.value !== 'course') {
      course.firmCode = firmCode.value;
    }

    const baseCourseId =
      course?.activityName === 'Conversational Learning'
        ? extractBaseClCourse(yaml.value)
        : extractBaseCourse(yaml.value);

    const baseCourse = await getLatestDraftOrSaved({
      courseId: baseCourseId ?? course.courseId
    });

    if (!baseCourse) {
      console.warn(
        `Base course not found: ${course.courseId} or ${baseCourseId}`
      );
    }

    if (baseCourseId && baseCourseId !== course.courseId)
      course.baseCourseId = baseCourseId;

    const title =
      course?.activityName === 'Conversational Learning'
        ? extractClTitle(course?.yaml, baseCourse?.yaml)
        : extractTitle(course?.yaml, baseCourse?.yaml);
    if (title) course.title = title;
  }

  submitStatus.value = 'pending';

  try {
    await createNewDraft(course);
    await commitDraft({
      draft: course,
      commitMessage:
        type.value === 'resource'
          ? 'Resource created in the Author UI.'
          : 'Course created in the Author UI.'
    });
    submitStatus.value = 'success';

    if (type.value === 'resource') {
      // Navigate to resource metadata editor
      const resourceId = +courseId.value;
      const fileName = firmCode.value
        ? `${firmCode.value}_${resourceId}`
        : `${resourceId}`;

      router.push({
        path: `/resource-metadata/${fileName}`
      });
    } else {
      // Navigate to editor for courses
      const { firmCode, courseId } = course;
      const fileName = firmCode ? `${firmCode}_${courseId}` : `${courseId}`;
      setCourse(course);
      router.push(`/editor/${fileName}`);
    }
  } catch (e) {
    console.error(e);
    error.value = 'Error: ' + e.message;
    submitStatus.value = 'error';
  }
};
</script>
